import axios from 'axios'
import {
  AllocateCreditPayload,
  Credit,
  GetAdminCreditAllocationListPayload,
  GetAdminCreditAllocationListResponse,
  GetAllCreditListResponse,
  RevokeCreditPayload,
  GetWorkspaceCreditUsageStatsResponse
} from '@/api/Credit/types'
import { AhhaAxios } from '@/api/Base/types'
import { setToken, flattenResponseData } from '@/api/Base/interceptors'

const ROOT = '/credit'

const creditAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_CREDIT_API_HOST,
  headers: {
    'Content-Type': 'application/json',
  },
}) as AhhaAxios

creditAxiosInstance.interceptors.request.use(setToken)
creditAxiosInstance.interceptors.response.use(flattenResponseData)

export default class CreditApi {
  private agent = creditAxiosInstance

  registerWorkspaceCredit(workspaceId: string) {
    return this.agent.post(`${ROOT}/workspace-credit`, { workspaceId })
  }

  getAdminCreditInfo() {
    return this.agent.get<Credit>(`${ROOT}/admin`)
  }

  getAdminCreditAllocationList(payload: GetAdminCreditAllocationListPayload) {
    const {
      itemsPerPage = 50,
      page = 0,
      sortKey,
      sortDir,
      from,
      to,
      creditTransactionType,
    } = payload ?? {}
    return this.agent.get<GetAdminCreditAllocationListResponse>(`${ROOT}/user-credit-history`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        from,
        to,
        creditTransactionType,
      },
    })
  }

  getAllCreditList(payload: any) {
    const {
      itemsPerPage = 50,
      page = 0,
      sortKey,
      sortDir,
      search,
    } = payload ?? {}

    return this.agent.get<GetAllCreditListResponse>(`${ROOT}/list`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        search,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getWorkspaceCreditInfo(workspaceId: string) {
    return this.agent.get<Credit>(`${ROOT}/workspace/${workspaceId}`)
  }

  getWorkspaceCreditUsage(workspaceId: string, payload: any) {
    const {
      itemsPerPage = 50,
      page = 0,
      from, // iso string (dayjs startof('day').toISOString())
      to, // iso string (dayjs endof('day').toISOString())
      creditTransactionType, // CONSUME, RECOVER
      consumedUserIds, // comma separated user ids
      creditUsageCategoryType,
      creditUsageFeature,
    } = payload ?? {}
    return this.agent.get<any>(`${ROOT}/workspace/${workspaceId}/credit-usage`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        from,
        to,
        creditTransactionType,
        consumedUserIds,
        creditUsageCategoryType,
        creditUsageFeature,
      },
    })
  }

  getWorkspaceCreditHistoryList(workspaceId: string, payload: GetAdminCreditAllocationListPayload) {
    const {
      itemsPerPage = 50,
      page = 0,
      sortKey,
      sortDir,
      from,
      to,
      creditTransactionType,
    } = payload ?? {}
    return this.agent.get<GetAdminCreditAllocationListResponse>(`${ROOT}/workspace/${workspaceId}/credit-history`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        from,
        to,
        creditTransactionType,
        // search,
      },
    })
  }

  getWorkspaceCreditUsageStats(workspaceId: string, payload: any) {
    const {
      from, // iso string (dayjs startof('day').toISOString())
      to, // iso string (dayjs endof('day').toISOString())
      timezone, // ex. Asia/Seoul
    } = payload ?? {}
    return this.agent.get<GetWorkspaceCreditUsageStatsResponse>(`${ROOT}/workspace/${workspaceId}/usage-stats`, {
      params: {
        from,
        to,
        timezone,
      },
    })
  }

  downloadWorkspaceCreditUsage(workspaceId: string, payload: { from?: string; to?: string; type: string }) {
    const { from, to, type } = payload
    return this.agent.get<any>(`${ROOT}/workspace/${workspaceId}/credit-usage/download`, {
      params: {
        from,
        to,
        type,
      },
      responseType: 'blob',
    })
  }

  getWorkspaceMonthlyStats(workspaceId: string, payload: { timezone: string }) {
    const { timezone } = payload ?? {}
    return this.agent.get<any>(`${ROOT}/workspace/${workspaceId}/monthly-stats`, {
      params: {
        timezone,
      },
    })
  }

  getMonthlyFeatureUsage(payload: { from: string; to: string; timezone: string; workspaceId?: string }) {
    const { from, to, timezone, workspaceId } = payload ?? {}
    return this.agent.get<{consume: number, feature: string}[]>(`${ROOT}/monthly-feature-usage`, {
      params: {
        from,
        to,
        timezone,
        workspaceId,
      },
    })
  }

  getMonthlyUsageRanking(payload: { page?: number; numPerPage?: number; from: string; to: string; search?: string }) {
    const { page = 0, numPerPage = 20, from, to, search } = payload ?? {}
    return this.agent.get<any>(`${ROOT}/monthly-usage-ranking`, {
      params: {
        page,
        num_per_page: numPerPage,
        from,
        to,
        search,
      },
    })
  }

  downloadMonthlyUsageRanking(payload: { from?: string; to?: string; type: string }) {
    const { from, to, type } = payload
    return this.agent.get<any>(`${ROOT}/monthly-usage-ranking/download`, {
      params: {
        from,
        to,
        type,
      },
      responseType: 'blob',
    })
  }

  getCreditPricing() {
    return this.agent.get<any>(`${ROOT}/credit-pricing`)
  }
}
